import { BellRing } from 'lucide-react';
import { useEffect, useState } from 'react';

import { disablePush, enablePush, type PushState, pushState } from '../lib/push';
import { Button } from './ui';

/**
 * Whether this browser gets calendar reminders while it is closed.
 *
 * The web has only ever promised in-app reminders; this is the control that
 * changes that. It says plainly what turning it on means, because it is the one
 * thing in Cairn that sends an event's title and time anywhere.
 */
export function ReminderDelivery() {
  const [state, setState] = useState<PushState>({ supported: false }),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState('');

  useEffect(() => {
    void pushState().then(setState);
  }, []);

  if (!state.supported) {
    return null;
  }

  const change = async (enable: boolean) => {
    setBusy(true);
    setMessage('');

    if (enable) {
      const result = await enablePush();

      setMessage(result.ok ? 'This browser will be reminded.' : result.reason);
    } else {
      await disablePush();
      setMessage('Reminders here are off. They still appear while Cairn is open.');
    }

    setState(await pushState());
    setBusy(false);
  };

  return (
    <section className="detail-card">
      <h2>
        <BellRing size={18} aria-hidden="true" /> Closed-app reminders
      </h2>
      <p className="muted">
        {state.subscribed
          ? 'Appointment and birthday reminders reach this browser even when Cairn is closed. Their title and time pass through your browser vendor’s push service on the way, encrypted so that the service relays them without being able to read them.'
          : 'Reminders currently appear only while Cairn is open. Turning this on lets the server reach this browser when it is closed.'}
      </p>
      {state.blocked && !state.subscribed ? (
        <p className="muted">
          Notifications are blocked for this site, so the browser will not ask again. Allow them in
          the site settings first.
        </p>
      ) : null}
      <div className="button-row wrap">
        <Button
          variant={state.subscribed ? 'secondary' : 'primary'}
          disabled={busy || (state.blocked && !state.subscribed)}
          onClick={() => void change(!state.subscribed)}
        >
          {state.subscribed ? 'Stop reminding this browser' : 'Remind this browser'}
        </Button>
      </div>
      {message ? <p className="muted">{message}</p> : null}
    </section>
  );
}

/**
 * The Mac app's reminders, in place of the browser's push control: Electron
 * cannot receive Web Push, so the app shows them itself from what the page
 * publishes (docs/desktop-app.md). Nothing leaves this Mac to do it.
 */
export function DesktopReminders() {
  return (
    <section className="detail-card">
      <h2>
        <BellRing size={18} aria-hidden="true" /> Reminders on this Mac
      </h2>
      <p className="muted">
        Appointment and birthday reminders appear as Mac notifications while Cairn is running, with
        its window closed too — it stays in the menu bar. Turn on Open at Login in the Cairn menu so
        they are there after a restart, and allow Cairn in System Settings → Notifications.
      </p>
    </section>
  );
}
